import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Crown, Shield, Gem, Clock, Star } from "lucide-react";
import { useState, useEffect } from "react";

const CTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('cta');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const trustBadges = [
    { icon: Crown, text: "Invite-Only" },
    { icon: Shield, text: "Zero Risk" },
    { icon: Gem, text: "0% Fees" },
    { icon: Star, text: "Premium Support" },
  ];

  return (
    <section id="cta" className="py-24 md:py-32 bg-background relative overflow-hidden">
      {/* Enhanced premium gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-card via-background to-background" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-to-b from-primary/20 via-secondary/10 to-transparent rounded-full blur-[180px]" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] bg-gradient-to-tr from-accent/15 via-tertiary/10 to-transparent rounded-full blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] bg-gradient-to-tl from-secondary/12 via-primary/8 to-transparent rounded-full blur-[140px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className={`max-w-4xl mx-auto text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Exclusive badge with gradient */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/15 via-secondary/10 to-accent/15 border border-primary/40 backdrop-blur-sm shadow-[0_0_30px_rgba(217,70,239,0.2)]">
              <Clock className="w-4 h-4 text-primary animate-pulse" />
              <span className="text-sm font-semibold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Limited spots available this month</span>
            </div>
          </div>

          {/* Main content */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Ready to Join the
            <br />
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_auto]">
              Elite?
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-4">
            Stop giving away 20-30% of your earnings. Start building real wealth.
          </p>

          <p className="text-base text-foreground/70 max-w-xl mx-auto mb-10">
            Applications reviewed within 48 hours. No commitment required. 
            If you're serious about your TikTok Live career, we want to hear from you.
          </p>
          
          {/* CTA Button */}
          <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link to="/apply">
              <Button 
                variant="premium" 
                size="lg" 
                className="group text-lg px-12 py-8 font-bold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_80px_rgba(217,70,239,0.6)] animate-pulse-glow"
              >
                <Crown className="mr-2 w-5 h-5" />
                Apply for Exclusive Access
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          
          {/* Trust badges with gradients */}
          <div className={`flex flex-wrap justify-center gap-4 md:gap-6 mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              const gradients = [
                "from-primary/15 to-secondary/15",
                "from-secondary/15 to-tertiary/15",
                "from-tertiary/15 to-accent/15",
                "from-accent/15 to-primary/15"
              ];
              return (
                <div 
                  key={index} 
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border border-border/50 bg-gradient-to-r ${gradients[index]} backdrop-blur-sm hover:border-primary/40 hover:shadow-[0_0_20px_rgba(217,70,239,0.15)] transition-all duration-300`}
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground/80">{badge.text}</span>
                </div>
              );
            })}
          </div>

          {/* Social proof reminder */}
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
                <span><strong className="text-foreground">100+</strong> elite creators</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                <span><strong className="text-foreground">$10M+</strong> paid in 2024</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-secondary animate-pulse"></span>
                <span><strong className="text-foreground">&lt;5%</strong> acceptance rate</span>
              </span>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default CTA;
